import { deleteAolItem, getAllAolItems } from 'src/api/aol/offlineAolApi';
import useGetAolData from 'src/api/aol/useGetAolData';
import useApi from 'src/api/useApi';
import { useAolStore } from 'src/context/AolContext';
import { formatAolUrlDate } from '../aol/aolDateUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { useSWService } from 'src/api/sw/useSWService';

const useSyncOfflineAolItems = () => {
    const { postApiData } = useApi();
    const { refreshRequestsContaining } = useSWService();
    const { getAolData } = useGetAolData();
    const { aolFilter } = useAolStore();
    const { selectedVessel } = useVesselStore();
    const syncAolItems = async (skipGetAolData?: boolean | null): Promise<void> => {
        try {
            const aolItems = await getAllAolItems();
            aolItems.sort((a, b) => a.lastUpdated - b.lastUpdated);

            for (const item of aolItems) {
                // Support both GUID keys (new) and integer ID keys (old, transition compat)
                const rawGuid = (item as any).noonReportGuid;
                const validGuid = rawGuid && rawGuid !== 'undefined' && rawGuid !== 'null' ? rawGuid : null;
                const itemKey = validGuid ?? item.noonReportId;

                if (!itemKey) {
                    console.warn('syncAolItems: Skipping item with no valid key — removing from store.', item);
                    try {
                        await deleteAolItem(rawGuid ?? item.noonReportId);
                    } catch (_) {
                        /* best effort */
                    }
                    continue;
                }

                let allRequestsSuccessful = true;
                // Track synced noonReportId within this item's sync (for section URL patching)
                let syncedNoonReportId: number | undefined = item.noonReportId;

                try {
                    const requests = [...(item.saveNoonReportSectionRequests ?? []), ...(item?.updateSectionStatusRequests ?? [])];

                    if (item.deleteNoonReportRequest) {
                        const response = await postApiData({
                            fullUrl: item.deleteNoonReportRequest.url,
                            data: item.deleteNoonReportRequest.body,
                            skipRefreshCacheEndpoints: true,
                        });
                        if (!response) {
                            allRequestsSuccessful = false;
                        }
                    } else {
                        if (item.addNoonReportRequest) {
                            const response = await postApiData({
                                fullUrl: item.addNoonReportRequest.url,
                                data: item.addNoonReportRequest.body,
                                skipRefreshCacheEndpoints: true,
                            });
                            if (response?.metadata?.noonReportId) {
                                syncedNoonReportId = response.metadata.noonReportId;
                            } else {
                                allRequestsSuccessful = false;
                            }
                        } else if (!(item as any).noonReportGuid && item.noonReportId < 0) {
                            // Legacy: Invalid state — offline-created report (negative ID) without addNoonReportRequest
                            console.warn(
                                `Cannot sync offline noon report sections: noonReportId=${item.noonReportId} has no addNoonReportRequest. ` +
                                    'This item will be removed to prevent repeated sync failures.',
                                item
                            );
                            await deleteAolItem(itemKey);
                            continue;
                        }

                        if (allRequestsSuccessful) {
                            for (const request of requests) {
                                if (!request.body || Object.keys(request.body).length === 0) {
                                    continue;
                                }
                                // Patch noonReportId into section request URLs (backend still requires it)
                                const requestNoonReportId = +request.response.noonReportId;
                                if (syncedNoonReportId && (isNaN(requestNoonReportId) || requestNoonReportId !== syncedNoonReportId)) {
                                    if (request.body?.noonReportId) {
                                        request.body.noonReportId = syncedNoonReportId;
                                    }
                                    if (request.body?.sectionData?.noonReportId) {
                                        request.body.sectionData.noonReportId = syncedNoonReportId;
                                    }
                                }
                                const sectionResponse = await postApiData({
                                    fullUrl: request.url,
                                    data: request.body,
                                    skipRefreshCacheEndpoints: true,
                                });
                                if (!sectionResponse) {
                                    allRequestsSuccessful = false;
                                    break;
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.warn('An unexpected error occurred while syncing item.', item, error);
                    allRequestsSuccessful = false;
                }

                if (allRequestsSuccessful) {
                    await deleteAolItem(itemKey);
                }
            }

            if (!skipGetAolData && selectedVessel) {
                await getAolData(selectedVessel.imoNumber, formatAolUrlDate(aolFilter.fromDate), formatAolUrlDate(aolFilter.toDate), true);
                await refreshRequestsContaining(['getNoonReportsForShip', 'getNoonReportStatuses', 'getNoonReportSection', 'getNoonReportComments']);
            }
        } catch (error) {
            console.warn(error);
        }
    };

    return {
        syncAolItems,
    };
};

export default useSyncOfflineAolItems;
